import { useEffect, useState } from 'react';
import { StyledPrimeReactInput } from 'styles/v2/Styles.styled';
import SmartLabel, { TSmartLabelProps } from '../labels/SmartLabel';
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primereact/autocomplete';
import useIsReadOnlyMode from '../compiler/hooks/useIsReadOnlyMode';

type TProps = Partial<TSmartLabelProps> & {
  value: any;
  name: string;
  onChanged?: any;
  triggerOnChangedFor?: 'keypress' | 'blur';
  onSelectSuggested(value: any): void;
  disabled?: boolean;
  placeholder?: string;
  suggestions?: any[];
  completeMethod?(event: AutoCompleteCompleteEvent): void;
  onSubmit?(): void;
} & React.InputHTMLAttributes<HTMLInputElement>;
const WizardTextInputWithAutoComplete: React.FunctionComponent<TProps> = ({
  label,
  tooltip,
  tooltipPosition,
  labelStyle,
  name,
  value,
  onChanged,
  triggerOnChangedFor = 'blur',
  onSelectSuggested,
  disabled,
  placeholder,
  required,
  error,
  suggestions,
  completeMethod,
  onSubmit,
  onBlur,
  maxLength,
}) => {
  const [selectedValue, setSelectedValue] = useState<string>(value);
  const isReadOnly = useIsReadOnlyMode() || disabled;
  const [keyToHideOverlay, setKeyToHideOverlay] = useState<number>(0);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      {label && (
        <SmartLabel
          label={label}
          required={required}
          labelStyle={labelStyle}
          tooltip={tooltip}
          tooltipPosition={tooltipPosition}
          hasValue={!!value}
          error={error}
        />
      )}
      <StyledPrimeReactInput
        as={AutoComplete}
        key={keyToHideOverlay}
        value={selectedValue}
        suggestions={suggestions}
        completeMethod={completeMethod}
        onBlur={(e: any) => {
          onBlur?.(e);
          e.target.value !== value?.toString() && onChanged(name, selectedValue);
        }}
        onSelect={(e: AutoCompleteSelectEvent) => onSelectSuggested(e.value)}
        onChange={(e: AutoCompleteChangeEvent) => {
          setSelectedValue(e.value);
          if (triggerOnChangedFor === 'keypress') {
            onChanged(name, e.value);
          }
        }}
        disabled={isReadOnly}
        placeholder={isReadOnly ? undefined : placeholder}
        onKeyPress={
          onSubmit === undefined
            ? undefined
            : (ev) => {
                if (ev.key === 'Enter') {
                  ev.preventDefault();
                  ev.stopPropagation();
                  if ('blur' in ev.target && typeof ev.target.blur === 'function') {
                    ev.target.blur();
                  }
                  // HACK: this hides dropdown overlay, spinner, and cancels throttled 'completeMethod' calls
                  setKeyToHideOverlay((old) => old + 1);
                  onSubmit();
                }
              }
        }
        maxLength={maxLength}
      />
    </div>
  );
};

export default WizardTextInputWithAutoComplete;
